import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, FieldBlock, LoadingState, Select, useToast } from '@orbind/ui'
import { rotuloDeMinutos } from '@orbind/core'
import { api } from '../../lib/api.ts'

/**
 * Console > Configurações — parametros da INSTALACAO inteira.
 *
 * Diferente de Planos (o que cada conta comprou) e de Feature toggles (o que
 * esta ligado): aqui sao comportamentos que valem para todo mundo e que so o
 * super admin muda.
 */

/**
 * Tempos oferecidos, de 1 minuto a 1 hora.
 *
 * Lista fechada em vez de campo livre: o parametro aceita 1..60, e um campo
 * numerico convidaria a digitar 0 ou 90 so para receber erro. Os degraus
 * cobrem o que se usa na pratica.
 */
const TEMPOS = [1, 2, 5, 10, 15, 20, 30, 45, 60] as const

export function ConsoleSettingsSection() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const q = useQuery({
    queryKey: ['console', 'settings'],
    queryFn: async () => (await api.console.settings.$get()).json(),
  })

  const [minutos, setMinutos] = useState<number | null>(null)
  // Semeia o formulario quando os dados chegam, sem derrubar o que o usuario
  // ja escolheu numa revalidacao.
  useEffect(() => {
    if (q.data && minutos === null) setMinutos(q.data.minutosAteAusente)
  }, [q.data, minutos])

  const salvar = useMutation({
    mutationFn: async (valor: number) => (await api.console.settings.$put({ json: { minutosAteAusente: valor } })).json(),
    onSuccess: (dados) => {
      queryClient.setQueryData(['console', 'settings'], dados)
      setMinutos(dados.minutosAteAusente)
      toast.push({ tone: 'success', message: 'Configuração salva. Vale imediatamente para quem está com o app aberto.' })
    },
    onError: () => {
      toast.push({ tone: 'danger', message: 'Não foi possível salvar a configuração.' })
    },
  })

  if (q.isPending) return <LoadingState variant="detail" label="Carregando configurações" />

  if (q.isError || !q.data) {
    return (
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Configurações</h1>
          <p className="adm-head__desc">Não foi possível carregar as configurações da instalação.</p>
        </div>
      </div>
    )
  }

  const salvo = q.data.minutosAteAusente
  const atual = minutos ?? salvo
  const alterado = atual !== salvo

  return (
    <>
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Configurações</h1>
          <p className="adm-head__desc">
            Parâmetros de comportamento que valem para toda a instalação do Orbind.
          </p>
        </div>
      </div>

      <div className="cons-panel">
        <h2 className="cons-panel__title">Presença</h2>

        <FieldBlock
          label="Tempo até ficar ausente"
          hint="Depois desse tempo sem mexer no computador, a pessoa aparece como Ausente para os colegas. Ela volta para Disponível assim que mexer no mouse ou no teclado."
        >
          {({ id }) => (
            <Select
              id={id}
              value={String(atual)}
              onChange={(valor) => setMinutos(Number(valor))}
              options={TEMPOS.map((m) => ({ value: String(m), label: rotuloDeMinutos(m) }))}
            />
          )}
        </FieldBlock>

        <div className="cons-settings__acoes">
          {alterado ? <span className="adm-hint">Alteração não salva</span> : null}
          <Button
            variant="primary"
            disabled={!alterado || salvar.isPending}
            onClick={() => salvar.mutate(atual)}
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>
    </>
  )
}
